/**
 * PDF Generation using jsPDF Direct API
 * Constructs PDF programmatically without HTML rendering
 * Provides reliable layout, pagination, and footer placement
 */
import jsPDF from 'jspdf';

// PDF Constants
const PDF_CONFIG = {
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    margin: { top: 15, left: 15, right: 15, bottom: 20 },
    pageHeight: 277, // A4 height 297mm - 20mm margins
    pageWidth: 180, // A4 width 210mm - 30mm margins
};

// Colors
const COLORS = {
    darkBlue: { r: 0, g: 31, b: 63 },      // #001f3f
    darkGray: { r: 51, g: 51, b: 51 },     // #333
    mediumGray: { r: 102, g: 102, b: 102 }, // #666
    lightGray: { r: 240, g: 240, b: 240 },  // #f0f0f0
    lightText: { r: 153, g: 153, b: 153 },  // #999
};

// Table Layout Configuration
const TABLE_CONFIG = {
    rowHeight: 6,
    headerHeight: 6,
    footerHeight: 20,
    cellPadding: 1,
    rightMargin: 30,
    footerOffset: 5,
};

/**
 * Main function: Generate invoice PDF using jsPDF direct API
 * @param {Object} invoiceData - Invoice record
 * @param {Array} serviceCharges - Service charge items
 * @param {Object} summary - Summary with subtotal, totalAdditionalCharges, totalDue
 * @param {Object} additionalData - Additional data (displayCompany, displaySeller, etc.)
 */
/**
 * Legacy wrapper for DOM-based PDF generation
 * Note: This is deprecated. Use generateInvoicePDFDirect() with invoice data instead.
 * Kept for backward compatibility with legacy components.
 * 
 * @param {HTMLElement} element - Invoice HTML element to convert
 * @param {string} invoiceNumber - Invoice number for filename
 * @returns {void}
 * @deprecated Use generateInvoicePDFDirect instead
 */
export const generateInvoicePDF = async (element, invoiceNumber) => {
    console.warn(
        '⚠️ generateInvoicePDF() is deprecated. Please use generateInvoicePDFDirect() instead with invoice data.',
        'Legacy DOM-based approach has reliability issues.'
    );

    if (!element) {
        console.error('Invoice element not found');
        alert('Unable to generate PDF. Invoice content not found.');
        return;
    }

    try {
        if (!globalThis?.html2pdf) {
            console.error('html2pdf library not available');
            alert('PDF library not available. Please refresh the page.');
            return;
        }

        const pdf = globalThis.html2pdf();
        pdf.set({
            margin: [10, 10, 10, 10],
            filename: `Invoice-${invoiceNumber}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: false,
            },
            jsPDF: {
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
                compress: true,
            },
            pagebreak: {
                mode: ['css'],
                avoid: ['.invoice-branding-section', '.bill-from-to-section', '.period-cards-section', '.invoice-summary-section'],
            },
        })
            .from(element)
            .save()
            .then(() => {
                console.log('✓ PDF generated (legacy):', `Invoice-${invoiceNumber}.pdf`);
            })
            .catch(error => {
                console.error('✗ Error saving PDF:', error);
                alert('Failed to download invoice PDF. Please try again.');
            });
    } catch (error) {
        console.error('✗ Error generating PDF:', error);
        alert('Failed to generate invoice PDF. Please try again.');
    }
};

export const generateInvoicePDFDirect = async (invoiceData, serviceCharges, summary, additionalData = {}) => {
    try {
        if (!invoiceData?.invoiceNumber) {
            console.error('Invalid invoice data');
            alert('Unable to generate PDF. Invoice data is missing.');
            return;
        }

        console.log('Generating PDF (jsPDF Direct) for invoice:', invoiceData.invoiceNumber);

        const {
            displayCompany = invoiceData.companyName || 'N/A',
            displaySeller = invoiceData.sellerName || '',
            periodFromDate = 'N/A',
            periodToDate = 'N/A',
            hasRentItems = false,
            taskDetails = null,
            print = false,
            hideEmptyColumns = false,
        } = additionalData;

        const warehouseName = invoiceData.warehouseName || 'N/A';
        const sellerCode = invoiceData.sellerCode || 'N/A';

        // Initialize PDF document
        const doc = new jsPDF({
            orientation: PDF_CONFIG.orientation,
            unit: PDF_CONFIG.unit,
            format: PDF_CONFIG.format,
        });

        let yPosition = PDF_CONFIG.margin.top;
        const pageHeight = PDF_CONFIG.pageHeight;
        const margin = PDF_CONFIG.margin;

        // ===== SECTION 1: Invoice Header =====
        yPosition = addInvoiceHeader(doc, invoiceData, displayCompany, displaySeller, yPosition);
        yPosition += 5; // Spacing

        // ===== SECTION 2: Bill From / To =====
        yPosition = addBillFromTo(
            doc,
            invoiceData,
            displayCompany,
            displaySeller,
            sellerCode,
            warehouseName,
            yPosition
        );
        yPosition += 5;

        // ===== SECTION 3: Period & Cycle (Task Details for task invoices) =====
        yPosition = taskDetails
            ? addTaskDetails(doc, taskDetails, yPosition)
            : addPeriodAndCycle(doc, periodFromDate, periodToDate, hasRentItems, yPosition);
        yPosition += 8;

        // ===== SECTION 4: Service Charges Table =====
        yPosition = addServiceChargesTable(doc, serviceCharges, yPosition, pageHeight, margin, hideEmptyColumns);
        yPosition += 5;

        // ===== SECTION 5: Summary =====
        addSummary(doc, summary, yPosition, pageHeight, margin);

        // ===== SECTION 6: Footer =====
        addFooter(doc, pageHeight);

        // Save PDF
        const fileName = `Invoice-${invoiceData.invoiceNumber}.pdf`;
        if (print) {
            doc.autoPrint();
            // Pop-up blocked: fall back to a download so the user still gets the invoice.
            if (!globalThis.open(doc.output('bloburl'), '_blank')) doc.save(fileName);
        } else {
            doc.save(fileName);
        }
        console.log('✓ PDF generated successfully (jsPDF Direct):', fileName);
    } catch (error) {
        console.error('✗ Error generating PDF (jsPDF Direct):', error);
        alert('Failed to generate invoice PDF. Please try again.');
    }
};

/**
 * Helper: Add invoice header section
 */
const addInvoiceHeader = (doc, invoiceData, company, seller, yPosition) => {
    setFontStyle(doc, 18, 'bold', COLORS.darkBlue);
    doc.text(`Invoice ${invoiceData.invoiceNumber}`, PDF_CONFIG.margin.left, yPosition);
    yPosition += 8;

    setFontStyle(doc, 13, 'normal', COLORS.mediumGray);
    const companyText = seller ? `${company} — ${seller}` : company;
    doc.text(companyText, PDF_CONFIG.margin.left, yPosition);
    yPosition += 6;

    setFontStyle(doc, 11, 'normal', COLORS.lightText);
    const issuedText = `Invoice Date: ${invoiceData.invoiceDate || 'N/A'}`;
    doc.text(issuedText, PDF_CONFIG.margin.left, yPosition);
    yPosition += 6;

    return yPosition;
};

/**
 * Helper: Add Bill From / To section
 */
const addBillFromTo = (doc, invoiceData, company, seller, sellerCode, warehouseName, yPosition) => {
    const colWidth = (PDF_CONFIG.pageWidth - 20) / 2; // Two columns
    const leftX = PDF_CONFIG.margin.left;
    const rightX = PDF_CONFIG.margin.left + colWidth + 10;
    const startY = yPosition;

    // BILL FROM (Left Column)
    setFontStyle(doc, 12, 'bold', COLORS.darkGray);
    doc.text('BILL FROM', leftX, yPosition);
    yPosition += 5;

    setFontStyle(doc, 11, 'normal', COLORS.mediumGray);
    doc.text('3PLVision Warehouse Operations', leftX, yPosition);
    yPosition += 4;
    doc.text(`Warehouse: ${warehouseName}`, leftX, yPosition);
    yPosition += 4;
    doc.text('info@3plvision.com', leftX, yPosition);
    yPosition += 4;
    doc.text('WhatsApp: +1 732-277-2073', leftX, yPosition);

    // BILL TO (Right Column)
    yPosition = startY;
    setFontStyle(doc, 12, 'bold', COLORS.darkGray);
    doc.text('BILL TO', rightX, yPosition);
    yPosition += 5;

    setFontStyle(doc, 11, 'normal', COLORS.mediumGray);
    doc.text(company, rightX, yPosition);
    yPosition += 4;
    if (seller) {
        doc.text(`Seller: ${seller}`, rightX, yPosition);
        yPosition += 4;
    }
    (invoiceData.billToAddressLines || []).forEach(line => {
        const wrapped = typeof doc.splitTextToSize === 'function' ? doc.splitTextToSize(line, colWidth) : [line];
        doc.text(wrapped, rightX, yPosition);
        yPosition += 4 * wrapped.length;
    });
    doc.text(`Seller Code: ${sellerCode}`, rightX, yPosition);

    // Fixed 20mm unless Bill To address lines need more room.
    return Math.max(startY + 20, yPosition + 6);
};

/**
 * Helper: Add Period & Cycle section
 */
const addPeriodAndCycle = (doc, fromDate, toDate, hasRentItems, yPosition) => {
    setFontStyle(doc, 12, 'normal', COLORS.mediumGray);

    const periodText = `Period: ${formatDate(fromDate)} to ${formatDate(toDate)}`;
    doc.text(periodText, PDF_CONFIG.margin.left, yPosition);
    yPosition += 5;

    const cycleText = `Cycle: ${hasRentItems ? 'Monthly' : 'Daily'}`;
    doc.text(cycleText, PDF_CONFIG.margin.left, yPosition);
    yPosition += 5;

    return yPosition;
};

/**
 * Helper: Add Task Details section (replaces Period & Cycle on task invoices)
 * @param {Array<string>} lines - e.g. ['Task ID: 10', 'Task Type: Labeling', ...]
 */
const addTaskDetails = (doc, lines, yPosition) => {
    setFontStyle(doc, 12, 'bold', COLORS.darkGray);
    doc.text('TASK DETAILS', PDF_CONFIG.margin.left, yPosition);
    yPosition += 5;

    setFontStyle(doc, 12, 'normal', COLORS.mediumGray);
    lines.forEach(line => {
        const wrapped = typeof doc.splitTextToSize === 'function' ? doc.splitTextToSize(line, PDF_CONFIG.pageWidth) : [line];
        doc.text(wrapped, PDF_CONFIG.margin.left, yPosition);
        yPosition += 5 * wrapped.length;
    });

    return yPosition;
};

/**
 * Helper: Add service charges table (handles multi-page)
 */
const addServiceChargesTable = (doc, charges, yPosition, pageHeight, margin, hideEmptyColumns = false) => {
    const footerHeight = 20; // Space reserved for footer
    const cellPadding = 3;

    const columns = getTableColumns(charges, hideEmptyColumns);
    const width = key => columns.find(col => col.key === key)?.width || 0;
    const totalTableWidth = columns.reduce((sum, col) => sum + col.width, 0);
    const wrapText = (text, colWidth) =>
        typeof doc.splitTextToSize === 'function' ? doc.splitTextToSize(text, colWidth - cellPadding * 2) : [text];

    // Table header
    yPosition = addTableHeader(doc, yPosition, columns, margin);
    yPosition += 1; // Minimal spacing between header and first row

    // Table rows
    charges.forEach((charge, idx) => {
        const skuTrackingLines = [
            ...wrapText(String(charge.sku || 'N/A'), width('ref')),
            ...wrapText(String(charge.ref || 'N/A'), width('ref')),
        ];
        // Long service names (e.g. "Custom Service (Kitting)") wrap instead of being cut off.
        const serviceValue = [
            ...wrapText(String(charge.service || ''), width('service')),
            ...(charge.outboundNumber ? wrapText(`(${charge.outboundNumber})`, width('service')) : []),
            ...(charge.referenceDetail ? wrapText(String(charge.referenceDetail), width('service')) : []),
        ];
        const wrappedLineCount = Math.max(
            columns.some(col => col.key === 'ref') ? skuTrackingLines.length : 0,
            Array.isArray(serviceValue) ? serviceValue.length : 0
        );
        const rowHeight = Math.max(TABLE_CONFIG.rowHeight, wrappedLineCount * 4 + 2);

        // Check if new page needed
        if (yPosition + rowHeight + 5 > pageHeight - footerHeight) {
            doc.addPage();
            yPosition = margin.top;
            yPosition = addTableHeader(doc, yPosition, columns, margin);
            yPosition += 1; // Minimal spacing between header and first row
        }

        // Alternate row colors
        const bgColor = idx % 2 === 0 ? [255, 255, 255] : [249, 249, 249];
        doc.setFillColor(...bgColor);
        doc.rect(
            margin.left,
            yPosition,
            totalTableWidth,
            rowHeight,
            'F'
        );

        // Row content (vertically centered within the row height)
        setFontStyle(doc, 9, 'normal', COLORS.mediumGray);
        const cellValues = {
            id: String(idx + 1).substring(0, 8),
            service: serviceValue,
            ref: skuTrackingLines,
            txn: String(charge.txn || '').substring(0, 12),
            unit: String(charge.unit || '').substring(0, 8),
            qty: String(charge.qty || ''),
            rate: charge.rate === null ? '-' : `$${charge.rate.toFixed(2)}`,
            add: `$${(charge.add || 0).toFixed(2)}`,
            amount: `$${(charge.amount || 0).toFixed(2)}`,
        };
        const textBaselineY = yPosition + rowHeight / 2 + 1; // Vertically centered

        let xPos = margin.left;
        columns.forEach(col => {
            const val = cellValues[col.key];
            // Wrapped (array) values start at the top of the row; compact fields stay single-line.
            doc.text(val, getCellTextX(xPos, col, cellPadding), Array.isArray(val) ? yPosition + 4 : textBaselineY, {
                align: col.align,
            });
            xPos += col.width;
        });

        yPosition += rowHeight;
    });

    return yPosition;
};

// Column widths total 166mm (14mm margin buffer).
const TABLE_COLUMNS = [
    { key: 'id', header: '#', width: 12, align: 'left' },
    { key: 'service', header: 'SERVICE', width: 32, align: 'left' },
    { key: 'ref', header: 'SKU (Tracking #)', width: 36, align: 'left', hasData: c => hasCellValue(c.sku) || hasCellValue(c.ref) },
    { key: 'txn', header: 'TXN', width: 20, align: 'left', hasData: c => hasCellValue(c.txn) },
    { key: 'unit', header: 'UNIT', width: 12, align: 'left', hasData: c => hasCellValue(c.unit) },
    { key: 'qty', header: 'QTY', width: 10, align: 'center' },
    { key: 'rate', header: 'RATE', width: 14, align: 'right' },
    { key: 'add', header: 'ADD.', width: 14, align: 'right', hasData: c => Number(c.add) > 0 },
    { key: 'amount', header: 'AMOUNT', width: 16, align: 'right' },
];

const hasCellValue = value => {
    const text = String(value ?? '').trim();
    return text !== '' && text !== 'N/A';
};

/**
 * Columns to render. With hideEmptyColumns, optional columns that no charge has data for are
 * dropped and their width is given to SERVICE so the table keeps its full width.
 */
const getTableColumns = (charges, hideEmptyColumns) => {
    if (!hideEmptyColumns) return TABLE_COLUMNS;
    const visible = TABLE_COLUMNS.filter(col => !col.hasData || charges.some(col.hasData));
    const freedWidth = TABLE_COLUMNS.reduce((sum, col) => sum + col.width, 0) - visible.reduce((sum, col) => sum + col.width, 0);
    return visible.map(col => (col.key === 'service' ? { ...col, width: col.width + freedWidth } : col));
};

const getCellTextX = (xPos, col, cellPadding) => {
    if (col.align === 'right') return xPos + col.width - cellPadding;
    if (col.align === 'center') return xPos + col.width / 2;
    return xPos + cellPadding;
};

/**
 * Helper: Add table header row
 */
const addTableHeader = (doc, yPosition, columns, margin) => {
    const cellPadding = 3;

    // Header background rectangle (positioned at correct Y)
    const totalWidth = columns.reduce((sum, col) => sum + col.width, 0);
    doc.setFillColor(COLORS.darkBlue.r, COLORS.darkBlue.g, COLORS.darkBlue.b);
    doc.rect(margin.left, yPosition, totalWidth, TABLE_CONFIG.headerHeight, 'F');

    // Header text (vertically centered within the rectangle)
    setFontStyle(doc, 9, 'bold', { r: 255, g: 255, b: 255 }); // White text
    const textBaselineY = yPosition + TABLE_CONFIG.headerHeight / 2 + 1; // Center text in rectangle

    let xPos = margin.left;
    columns.forEach(col => {
        doc.text(col.header, getCellTextX(xPos, col, cellPadding), textBaselineY, { align: col.align });
        xPos += col.width;
    });

    // Return Y position just below the header (for first data row)
    return yPosition + TABLE_CONFIG.headerHeight;
};

/**
 * Helper: Add summary section
 */
const addSummary = (doc, summary, yPosition, pageHeight, margin) => {
    const footerReserved = TABLE_CONFIG.footerHeight;
    const categoryTotals =
        Array.isArray(summary.categoryTotals) && summary.categoryTotals.length > 1 ? summary.categoryTotals : [];
    const summaryMinSpace = 35 + categoryTotals.length * 7; // Minimum space needed for summary section

    // Check if sufficient space remains on current page; if not, go to new page
    if (yPosition + summaryMinSpace > pageHeight - footerReserved) {
        doc.addPage();
        yPosition = margin.top;
    }

    // Add top margin to separate summary from table
    yPosition += 8;

    // Position summary on the right side of the page
    const labelWidth = 60;
    const rightMargin = 15; // Margin from right edge of page
    const summaryX = margin.left + PDF_CONFIG.pageWidth - rightMargin - labelWidth - 25;
    const valueX = summaryX + labelWidth;

    setFontStyle(doc, 12, 'normal', COLORS.mediumGray);

    for (const total of categoryTotals) {
        const amountText = `$${(total.amount || 0).toFixed(2)}`;
        const label = fitLabelBeforeValue(doc, `${total.category} (${total.count})`, summaryX, valueX, amountText);
        doc.text(label, summaryX, yPosition);
        doc.text(amountText, valueX, yPosition, { align: 'right' });
        yPosition += 7 + (Array.isArray(label) ? label.length - 1 : 0) * 5;
    }

    // Subtotal
    doc.text('Subtotal', summaryX, yPosition);
    doc.text(`$${(summary.subtotal || 0).toFixed(2)}`, valueX, yPosition, { align: 'right' });
    yPosition += 7;

    // Additional Charges
    doc.text('Additional Charges', summaryX, yPosition);
    doc.text(
        `$${(summary.totalAdditionalCharges || 0).toFixed(2)}`,
        valueX,
        yPosition,
        { align: 'right' }
    );
    yPosition += 7;

    // Tax (if present in summary)
    if (summary.tax !== undefined && summary.tax !== null) {
        doc.text('Tax (0%)', summaryX, yPosition);
        doc.text(`$${(summary.tax || 0).toFixed(2)}`, valueX, yPosition, { align: 'right' });
        yPosition += 7;
    }

    // Payment method (if present in summary)
    if (summary.paymentMethod) {
        const totalText = `$${(summary.totalDue || 0).toFixed(2)}`;
        const label = fitLabelBeforeValue(doc, `Total Amount - ${summary.paymentMethod}`, summaryX, valueX, totalText);
        doc.text(label, summaryX, yPosition);
        doc.text(totalText, valueX, yPosition, { align: 'right' });
        yPosition += 7 + (Array.isArray(label) ? label.length - 1 : 0) * 5;
    } else {
        // Default: Total Amount Due (with dark blue background)
        doc.setFillColor(COLORS.darkBlue.r, COLORS.darkBlue.g, COLORS.darkBlue.b);
        doc.rect(summaryX - 5, yPosition - 3, labelWidth + 50, 6, 'F');

        setFontStyle(doc, 12, 'bold', { r: 255, g: 255, b: 255 }); // White text
        doc.text('Total Amount Due', summaryX, yPosition);
        doc.text(`$${(summary.totalDue || 0).toFixed(2)}`, valueX, yPosition, {
            align: 'right',
        });
        yPosition += 7;
    }

    return yPosition;
};

// Wraps a summary label (into lines) only when it would otherwise run into its right-aligned value.
const fitLabelBeforeValue = (doc, label, labelX, valueRightX, valueText) => {
    if (typeof doc.splitTextToSize !== 'function' || typeof doc.getTextWidth !== 'function') {
        return label;
    }
    const gap = 4;
    const availableWidth = valueRightX - doc.getTextWidth(valueText) - gap - labelX;
    const lines = doc.splitTextToSize(label, availableWidth);
    return lines.length > 1 ? lines : label;
};

/**
 * Helper: Add footer section
 */
const addFooter = (doc, pageHeight) => {
    const footerY = pageHeight - TABLE_CONFIG.footerOffset;
    const footerLines = [
        'This invoice was automatically generated by 3PLVision.',
        'For billing queries contact info@3plvision.com',
        '© 2026 3PLVision. All rights reserved.',
    ];

    setFontStyle(doc, 10, 'normal', COLORS.lightText);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page++) {
        doc.setPage(page);
        let yFooter = footerY;

        footerLines.forEach(line => {
            doc.text(line, PDF_CONFIG.margin.left + PDF_CONFIG.pageWidth / 2, yFooter, { align: 'center' });
            yFooter += 4;
        });
    }
};

/**
 * Utility: Set font style
 */
const setFontStyle = (doc, size, weight, color) => {
    doc.setFontSize(size);
    doc.setFont('helvetica', weight === 'bold' ? 'bold' : 'normal');
    doc.setTextColor(color.r, color.g, color.b);
};

/**
 * Utility: Format date
 */
const formatDate = dateStr => {
    if (!dateStr || dateStr === 'N/A') return 'N/A';
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (error) {
        console.warn(`Failed to parse date "${dateStr}":`, error);
        return dateStr;
    }
};
